import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Search, Plus, Edit2, Trash2, Phone, Mail, Building2, MapPin, FileText, ChevronRight, X } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CustomerHistoryDialog } from "@/components/customers/CustomerHistoryDialog";

const CustomerManagement = ({ user, onLogout }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    company_name: "", gst_number: "", address: "",
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : "";
      const res = await api.get(`/customers${params}`);
      setCustomers(res.data.customers);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const resetForm = () => {
    setForm({ name: "", phone: "", email: "", company_name: "", gst_number: "", address: "" });
    setEditingCustomer(null);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    setForm({
      name: customer.name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      company_name: customer.company_name || "",
      gst_number: customer.gst_number || "",
      address: customer.address || "",
    });
    setOpenForm(true);
  };

  const openCustomerDetails = (customer) => {
    setSelectedCustomer(customer);
    setOpenDetails(true);
  };

  const saveCustomer = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    try {
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.customer_id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer created");
      }
      fetchCustomers();
      setOpenForm(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save customer");
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      await api.delete(`/customers/${customerId}`);
      toast.success("Customer deleted");
      setDeleteConfirm(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete customer");
      setDeleteConfirm(null);
    }
  };

  const formatTime12 = (time24) => {
    if (!time24) return "";
    const [h, m] = time24.split(":");
    const hour = parseInt(h, 10);
    const period = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${period}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      <BottomNav user={user} onLogout={onLogout} />

      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900">Customers</h1>
            <p className="text-sm text-slate-600 mt-1">{customers.length} customer{customers.length !== 1 ? "s" : ""}</p>
          </div>
          <Button
            data-testid="add-customer-btn"
            onClick={() => { resetForm(); setOpenForm(true); }}
            className="rounded-full bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs sm:text-sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Customer
          </Button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="customer-search"
              placeholder="Search by name, phone, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 rounded-xl text-sm"
            />
          </div>
        </div>

        {/* Customer List */}
        <div className="space-y-3">
          {customers.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">{searchQuery ? "No customers match your search" : "No customers yet. Add your first customer!"}</p>
            </div>
          ) : (
            customers.map((customer, index) => (
              <motion.div
                key={customer.customer_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:border-violet-200 transition-colors cursor-pointer"
                onClick={() => openCustomerDetails(customer)}
                data-testid={`customer-card-${index}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm truncate">{customer.name}</h3>
                      {customer.company_name && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-[10px] rounded-full truncate max-w-[120px]">{customer.company_name}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Phone className="w-3 h-3" /> {customer.phone}
                      </span>
                      {customer.email && (
                        <span className="flex items-center gap-1 text-xs text-slate-500 truncate">
                          <Mail className="w-3 h-3" /> {customer.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 w-8 p-0"
                      onClick={(e) => { e.stopPropagation(); openEdit(customer); }}
                      data-testid={`edit-customer-${index}`}
                    >
                      <Edit2 className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 w-8 p-0 text-red-500 border-red-200 hover:bg-red-50"
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(customer.customer_id); }}
                      data-testid={`delete-customer-${index}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-heading">Delete Customer?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">This action cannot be undone. Customers with active bookings cannot be deleted.</p>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => deleteCustomer(deleteConfirm)} className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-xs">Delete</Button>
            <Button onClick={() => setDeleteConfirm(null)} variant="outline" className="flex-1 rounded-xl text-xs">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Customer Dialog */}
      <Dialog open={openForm} onOpenChange={(v) => { if (!v) resetForm(); setOpenForm(v); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editingCustomer ? "Edit Customer" : "Add Customer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Customer name" className="h-10 rounded-xl text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Phone *</Label>
                <Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Mobile number" className="h-10 rounded-xl text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Optional" className="h-10 rounded-xl text-sm" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Company Name</Label>
                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Optional" className="h-10 rounded-xl text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">GST Number</Label>
                <Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} placeholder="Optional" className="h-10 rounded-xl text-sm" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Address</Label>
              <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Optional" className="rounded-xl text-sm" rows={2} />
            </div>
            <Button data-testid="save-customer-btn" onClick={saveCustomer} className="w-full rounded-xl h-11 bg-primary">
              {editingCustomer ? "Save Changes" : "Add Customer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Details + Full Rental History */}
      <CustomerHistoryDialog
        open={openDetails}
        onOpenChange={(o) => { setOpenDetails(o); if (!o) setSelectedCustomer(null); }}
        customer={selectedCustomer}
      />
    </div>
  );
};

export default CustomerManagement;
